<script setup lang="ts">
import { useRootStore } from '@/store';
import { Alert } from '@/store/classes';
import FormField from "@/components/common/FormField.vue3.vue"

import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';
import { useFormValidation } from './common/FormValidation';
import { RequiredStringSchema } from './common/schemas';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const AUTO_REFRESH_MS = 120000; // Refresh token 2 minutes before it expires if session is not locked

const router = useRouter();
const rootStore = useRootStore();

const setAccessToken = (token?: string) => rootStore.dispatch('setAccessToken', token);
const setFacilityId = (facilityId?: string) => rootStore.dispatch('setFacilityId', facilityId);
const showLoader = (loaderName: string) => rootStore.dispatch('showLoader', loaderName);
const hideLoader = async (loaderName: string) => rootStore.dispatch('hideLoader', loaderName);
const showAlert = (alert: Alert) => rootStore.dispatch('showAlert', alert);
const setSessionLocked = (locked: boolean) => rootStore.dispatch('setSessionLocked', locked);
const hideModal = () => rootStore.dispatch('hideModal');
const hideConfirmModal = () => rootStore.dispatch('hideConfirmModal');

const facility = computed(() => rootStore.state.facility);
const accessToken = computed(() => rootStore.state.accessToken);
const sessionTicker = computed(() => rootStore.state.sessionTicker);
const sessionLocked = computed(() => rootStore.state.sessionLocked);

const isStandaloneUserFacility = computed(() => rootStore.getters.isStandaloneUserFacility);

const lastActivity = ref(new Date())
const isRefreshing = ref(false);
const password = ref('');

const previousUserName = ref('');
const previousAppName = ref('');

const refreshActivity = () => lastActivity.value = new Date();

const Schema = z.object({ password: RequiredStringSchema })
const passwordModel = computed(() => ({ password: password.value }))
const validationState = useFormValidation<z.infer<typeof Schema>>(Schema, passwordModel, true);

onMounted(() => {
    refreshActivity();
    document.addEventListener('mousedown', refreshActivity);
    document.addEventListener('touchstart', refreshActivity);
})

watch(sessionTicker, async () => {
    const now = new Date();
    const sessionTimeout = lastActivity?.value.getTime() + facility.value.clientSideTimeout * 60000;
    if (!sessionLocked.value && accessToken && accessToken.value.value && sessionTimeout <= now.getTime()) {
        await lockSession();
    }
    if (!isRefreshing.value && !sessionLocked.value && accessToken.value && accessToken.value.tokenExpiration 
        && accessToken.value.tokenExpiration.getTime() - AUTO_REFRESH_MS <= now.getTime()
        && accessToken.value.tokenExpiration.getTime() > now.getTime()) {
        isRefreshing.value = true;
        await refreshToken();
    }
})

async function confirm() {
    await showLoader('inactivity');
    let hasLogin = false;
    if (!validationState.validate(true)) {
        return;
    }
    try {
        const authResponse = await axios({
            url: isStandaloneUserFacility.value ? '/auth/messaging' : '/auth/sso',
            method: 'post',
            data: {
                username: previousUserName.value,
                password: password.value,
                applicationName: previousAppName.value
            }
        });
        hasLogin = true;
        if (authResponse.data) {
            setAccessToken(authResponse.data);
            setSessionLocked(false);
        }
    } catch (e: any) {
        validationState.result.value.password.invalid = true;
        validationState.result.value.password.submitAttempted = true;
        validationState.result.value.password.message = (e?.response?.status === 401 && e.response.data)
            ? e.response.data
            : hasLogin ? 'Unable to log in, please try again later' : 'The password was incorrect'

    } finally {
        await hideLoader('inactivity');
    }
}

async function lockSession() {
    setSessionLocked(true);
    previousUserName.value = (accessToken.value?.username) ? accessToken.value.username : previousUserName.value;
    previousAppName.value = (accessToken.value?.appName) ? accessToken.value.appName : previousAppName.value;
    await setAccessToken();
}

async function logout() {
    hideModal();
    hideConfirmModal();
    await setAccessToken();
    await setFacilityId();
    setSessionLocked(false);
    router.push({
        path: '/login', 
        query: { facilityType: facility.value.externalFacilityType }
    });
}

async function refreshToken() {
    try {
        const authResponse = await axios.get<string>('/auth');
        if (authResponse && authResponse.data) {
            await setAccessToken(authResponse.data)
        }
    } catch (e) {
        console.error(e);
        showAlert({
            text: 'Failed to automatically renew session. Please reload and log in again.',
            type: 'error'
        });
        await setAccessToken();
    } finally {
        isRefreshing.value = false;
    }
}
</script>

<template>
    <p-dialog 
        :visible="sessionLocked"
        :closable="false"
        header="Inactivity Warning"
        modal
    >
        <p>
            You have been inactive for an extended period of time. Please re-enter your password to continue.
        </p>
        <form>
            <form-field
                v-slot="{ onBlur }"
                :validation-state="validationState.result.value.password"
                label-key="labels.user.password"
            >
                <p-input-text
                    v-model="password"
                    class="w-full"
                    type="password"
                    autofocus
                    @blur="onBlur"
                />
            </form-field>
        </form>
        <template #footer>
            <p-button
                class="p-button-outlined"
                @click="logout"
            >
                {{ t('actions.logout') }}
            </p-button>
            <p-button
                type="submit"
                @click.prevent="confirm"
            >
                {{ t('actions.continue') }}
            </p-button>
        </template>
    </p-dialog>
</template>
